import { Controller } from '@hotwired/stimulus'
import { formatDollars } from './pricing_number_format.js'

const ANNUAL_MULTIPLICATOR = 10 // 10 months billed annually (same as Plan::ANNUAL_MULTIPLICATOR)

// CSS classes toggled on plan buttons to show the selected state
// Active styling is driven by CSS variables set via builder settings (pricing-plan-btn--active)
const PLAN_ACTIVE_CLS = ['pricing-plan-btn--active']
const PLAN_INACTIVE_CLS = []

// CSS classes toggled on billing cycle buttons
// Active styling is driven by CSS variables set via builder settings (pricing-switch--active)
const BILLING_ACTIVE_CLS = ['pricing-switch--active']
const BILLING_INACTIVE_CLS = []

export default class extends Controller {
  static values = {
    plans: Array,
    contactCosts: Object,
    contactSteps: Object,
    texts: Object,
  }

  static targets = [
    'planButton',
    'contactsInput',
    'contactsSlider',
    'priceDisplay',
    'billingNote',
    'annualSavingsLabel',
    'annualSavingsText',
    'monthlyBtn',
    'annualBtn',
    'minContactsLabel',
    'maxContactsLabel',
    'enterpriseNote',
    'planMinNote',
  ]

  connect() {
    this._contactLevels = this._buildContactLevels()
    this._selectedPlanIndex = 0
    this._isAnnual = true
    this._liveContacts = null // contacts typed but not yet blurred; null means use slider
    this._isOverMax = false // true while the input holds a supra-max value

    this._initSlider()
    this._applyBillingCycle(true) // set button visual state before _selectPlan triggers _updatePrice
    this._selectPlan(0)
  }

  // ── Plan selection ─────────────────────────────────────────────────────────

  selectPlan(event) {
    const index = parseInt(event.params.index, 10)
    this._selectPlan(index)
  }

  _selectPlan(index) {
    this._selectedPlanIndex = index
    this.planButtonTargets.forEach((btn, i) => {
      const active = i === index
      btn.setAttribute('aria-pressed', active)
      if (active) {
        btn.classList.add(...PLAN_ACTIVE_CLS)
        btn.classList.remove(...PLAN_INACTIVE_CLS)
      } else {
        btn.classList.remove(...PLAN_ACTIVE_CLS)
        btn.classList.add(...PLAN_INACTIVE_CLS)
      }
    })
    this._applyPlanMinimum()
    this._updatePrice()
  }

  _applyPlanMinimum() {
    const plan = this.plansValue[this._selectedPlanIndex]
    if (!plan) return

    const minContacts = plan.contact_charge_threshold || this._contactLevels[0]
    const minIdx = this._contactLevels.findIndex(
      (level) => level >= minContacts
    )
    if (minIdx < 0) return

    // Use _currentContacts() to respect any live-typed value (not just the slider position).
    const currentContacts = this._currentContacts()

    this.contactsSliderTarget.min = minIdx
    this.contactsSliderTarget.setAttribute('aria-valuemin', minContacts)
    this.minContactsLabelTarget.textContent =
      minContacts.toLocaleString('en-US')

    if (currentContacts < minContacts) {
      this._liveContacts = null
      this._setSliderIdx(minIdx)
      this.contactsInputTarget.value =
        this._contactLevels[minIdx].toLocaleString('en-US')
      this.planMinNoteTarget.textContent = `Contact count adjusted to plan minimum: ${minContacts.toLocaleString(
        'en-US'
      )}`
    } else {
      this.planMinNoteTarget.textContent = ''
    }
  }

  // ── Billing cycle ──────────────────────────────────────────────────────────

  selectMonthly() {
    this._isAnnual = false
    this._applyBillingCycle()
  }

  selectAnnual() {
    this._isAnnual = true
    this._applyBillingCycle()
  }

  _applyBillingCycle(skipUpdate = false) {
    if (this._isAnnual) {
      this.annualBtnTarget.setAttribute('aria-pressed', 'true')
      this.monthlyBtnTarget.setAttribute('aria-pressed', 'false')
      this.annualBtnTarget.classList.add(...BILLING_ACTIVE_CLS)
      this.annualBtnTarget.classList.remove(...BILLING_INACTIVE_CLS)
      this.monthlyBtnTarget.classList.remove(...BILLING_ACTIVE_CLS)
      this.monthlyBtnTarget.classList.add(...BILLING_INACTIVE_CLS)
    } else {
      this.monthlyBtnTarget.setAttribute('aria-pressed', 'true')
      this.annualBtnTarget.setAttribute('aria-pressed', 'false')
      this.monthlyBtnTarget.classList.add(...BILLING_ACTIVE_CLS)
      this.monthlyBtnTarget.classList.remove(...BILLING_INACTIVE_CLS)
      this.annualBtnTarget.classList.remove(...BILLING_ACTIVE_CLS)
      this.annualBtnTarget.classList.add(...BILLING_INACTIVE_CLS)
    }

    if (!skipUpdate) this._updatePrice()
  }

  // ── Slider and input ───────────────────────────────────────────────────────

  onSliderChange() {
    this._liveContacts = null // slider is the source of truth once dragged
    this._isOverMax = false
    const idx = parseInt(this.contactsSliderTarget.value, 10)
    this._setSliderIdx(idx)
    // Use toLocaleString for display (type="text" — no browser number-format rejection)
    this.contactsInputTarget.value =
      this._contactLevels[idx].toLocaleString('en-US')
    this.enterpriseNoteTarget.classList.add('hidden')
    this._updatePrice()
  }

  // Fires on every keystroke — stores the live value so billing-cycle changes stay in sync.
  onContactsLiveInput() {
    const raw = this.contactsInputTarget.value
      .replace(/,/g, '')
      .replace(/\s/g, '')
    if (!/^\d+$/.test(raw)) {
      // Any non-numeric input (blank or e.g. "10k") — reset to slider-based price to avoid stale display
      this._liveContacts = null
      this._isOverMax = false
      this.enterpriseNoteTarget.classList.add('hidden')
      this._updatePrice()
      return
    }
    const contacts = parseInt(raw, 10)
    if (contacts <= 0) {
      this._liveContacts = null
      this._isOverMax = false
      this.enterpriseNoteTarget.classList.add('hidden')
      this._updatePrice()
      return
    }

    const maxContacts = this._contactLevels[this._contactLevels.length - 1]
    const isOverMax = contacts > maxContacts
    this._isOverMax = isOverMax
    this.enterpriseNoteTarget.classList.toggle('hidden', !isOverMax)

    if (isOverMax) {
      // Don't show a capped price for supra-max contacts — only the enterprise note applies
      this.priceDisplayTarget.textContent = '—'
      this.billingNoteTarget.textContent = ''
      this.annualSavingsTextTarget.textContent = ''
      this.annualSavingsLabelTarget.classList.add('invisible')
      this._liveContacts = null
      return
    }

    const plan = this.plansValue[this._selectedPlanIndex]
    if (!plan) return
    // Store raw typed value so plan switches re-floor correctly; floor only for price compute
    this._liveContacts = contacts
    const effective = Math.max(
      contacts,
      plan.contact_charge_threshold || this._contactLevels[0]
    )
    this._updatePriceForContacts(effective)
  }

  // Fires on blur/Enter — snaps to a valid level, reformats, and syncs the slider.
  onContactsInputChange() {
    const raw = this.contactsInputTarget.value
      .replace(/,/g, '')
      .replace(/\s/g, '')
    // Treat any non-integer string (e.g. "10k", "1e3") as blank → reset to minimum
    const contacts = /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN

    const minIdx = parseInt(this.contactsSliderTarget.min, 10) || 0
    const minLevel = this._contactLevels[minIdx]

    // On invalid / blank input, reset to the plan minimum
    const rawSnapped = isNaN(contacts) ? minLevel : this._snapToLevel(contacts)
    const snapped = rawSnapped < minLevel ? minLevel : rawSnapped
    const idx = this._contactLevels.indexOf(snapped)

    this._liveContacts = null // slider is now in sync; live override no longer needed
    this._isOverMax = false // snap always brings value into valid range; enterprise note only shows during live typing
    this.contactsInputTarget.value = snapped.toLocaleString('en-US')
    this._setSliderIdx(idx >= 0 ? idx : minIdx)
    this.enterpriseNoteTarget.classList.add('hidden')
    this._updatePrice()
  }

  _initSlider() {
    const maxIdx = this._contactLevels.length - 1
    this.contactsSliderTarget.max = maxIdx
    this.contactsSliderTarget.min = 0
    this.contactsSliderTarget.setAttribute(
      'aria-valuemin',
      this._contactLevels[0]
    )
    this.contactsSliderTarget.setAttribute(
      'aria-valuemax',
      this._contactLevels[maxIdx]
    )
    this._setSliderIdx(0)
    this.contactsInputTarget.value =
      this._contactLevels[0].toLocaleString('en-US')
    this.maxContactsLabelTarget.textContent =
      this._contactLevels[maxIdx].toLocaleString('en-US')
  }

  _setSliderIdx(idx) {
    this.contactsSliderTarget.value = idx
    this.contactsSliderTarget.setAttribute(
      'aria-valuetext',
      this._contactLevels[idx].toLocaleString('en-US') + ' contacts'
    )
  }

  // ── Price calculation ──────────────────────────────────────────────────────

  _updatePrice() {
    this._updatePriceForContacts(this._currentContacts())
  }

  _updatePriceForContacts(contacts) {
    if (this._isOverMax) return
    const plan = this.plansValue[this._selectedPlanIndex]
    if (!plan) return

    const extraCents = this._calculateContactCost(
      contacts,
      plan.contact_charge_threshold
    )
    const totalCents = plan.base_monthly_price_cents + extraCents

    let displayPrice, billingNote, savingsLabel

    if (this._isAnnual) {
      // Annual: 10 months charged, displayed as monthly equivalent (round to nearest cent)
      const annualMonthly = Math.round((totalCents * ANNUAL_MULTIPLICATOR) / 12)
      // 2 free months = (12 - ANNUAL_MULTIPLICATOR) × totalCents; matches Plan#flex_contacts_cents(annual: true) / 12
      const annualSavings = totalCents * 2
      displayPrice = formatDollars(annualMonthly, { grouping: false })
      billingNote = (
        this.textsValue.billed_annually ??
        'Billed annually (Save ${{amount}}/year)'
      ).replace('{{amount}}', formatDollars(annualSavings))
      savingsLabel = this.textsValue.big_savings ?? 'Big Savings!'
    } else {
      displayPrice = formatDollars(totalCents, { grouping: false })
      billingNote = this.textsValue.billed_monthly ?? 'Billed monthly'
      savingsLabel = ''
    }

    this.priceDisplayTarget.textContent = displayPrice
    this.billingNoteTarget.textContent = billingNote
    this.annualSavingsTextTarget.textContent = savingsLabel
    this.annualSavingsLabelTarget.classList.toggle('invisible', !savingsLabel)
  }

  _currentContacts() {
    if (this._liveContacts != null) return this._liveContacts
    const idx = parseInt(this.contactsSliderTarget.value, 10)
    return this._contactLevels[idx] || this._contactLevels[0]
  }

  // Mirrors Plan#flex_contacts_cents logic from plan.rb.
  //
  //   CONTACT_COSTS = { thresholds: { 500=>5, 1000=>10, …, 10000=>90 }, step: 2500, cost: 15 }
  //
  // Returns extra monthly cost in CENTS.
  _calculateContactCost(contacts, chargeThreshold) {
    if (chargeThreshold == null || contacts <= chargeThreshold) return 0

    const { thresholds, step, cost } = this.contactCostsValue
    const keys = Object.keys(thresholds)
      .map(Number)
      .sort((a, b) => a - b)

    for (const key of keys) {
      if (contacts <= key) return thresholds[key] * 100
    }

    // Beyond all named thresholds — continue in `step`-sized increments
    const maxKey = keys[keys.length - 1]
    const maxCost = thresholds[maxKey]
    const extra = contacts - maxKey
    const level = Math.floor((extra - 1) / step) + 1
    return (maxCost + level * cost) * 100
  }

  // ── Contact level generation ───────────────────────────────────────────────

  // Builds the discrete set of contact counts reachable via the slider,
  // using the same step map as the billing page incrementer.
  _buildContactLevels() {
    const steps = this.contactStepsValue
    const thresholds = Object.keys(steps)
      .map(Number)
      .sort((a, b) => a - b)
    const maxValue = thresholds[thresholds.length - 1]
    const levels = []
    let current = thresholds[0]

    while (current <= maxValue) {
      levels.push(current)
      if (current >= maxValue) break

      let increment = steps[thresholds[0]]
      for (const t of thresholds) {
        if (current >= t) increment = steps[t]
      }
      current = Math.min(current + increment, maxValue)
    }

    return levels
  }

  // Snap up to the first valid level >= value so the displayed price is never
  // lower than what the user would actually pay at their contact count.
  _snapToLevel(value) {
    return (
      this._contactLevels.find((level) => level >= value) ||
      this._contactLevels[this._contactLevels.length - 1]
    )
  }
}
